import { GameSession } from './GameSession';
import { PlayingField } from './PlayingField';
import { Player } from './Player';
import { Rock } from './Rock';
import { Screen } from './Screen';
import { Input } from './Input';
import { PLAY_SPEEDS, HIDDEN_FACTOR_PLAY_SPEED, NEW_LIFE_SCORE } from './Constants';
import { BOARD_WIDTH, BOARD_HEIGHT, REPLAY_WIDTH, REPLAY_HEIGHT, validateReplaySnapshot } from './Replay.mjs';

// Weak IDs preserve each rock's identity, including when two rocks overlap.
export class ReplaySampler {
    ids = new WeakMap(); nextId = 1;
    id(object) {
        if (!this.ids.has(object)) this.ids.set(object, this.nextId++);
        return this.ids.get(object);
    }
    capture(session) {
        const field = session?.field;
        if (!field) return null;
        const player = field.player;
        return {
            layout: field.layout.map(row => row.join('')).join(''),
            state: {
                board: this.id(field), name: field.name.replace(/[\x00-\x1f\x7f]/g, '').slice(0, 120),
                player: { x: player.x, y: player.y, state: player.state, nextState: player.nextState ?? null,
                    jumpStep: player.jumpStep, deathStep: player.deathStep },
                rocks: field.rocks.map(rock => ({ id: this.id(rock), x: rock.x, y: rock.y, state: rock.state,
                    nextState: rock.nextState ?? null, spawnStep: rock.spawnStep, deathStep: rock.deathStep })),
                status: { score: session.score, lives: session.lives, levelNumber: session.levelNumber,
                    time: field.time, playSpeed: session.playSpeed, paused: !!session.paused,
                    winning: !!field.winning, testing: !!session.testing, cheated: !!session.cheated,
                    testResult: session.testResult || null },
                resume: { difficulty: session.hiddenFactor(), nextLife: session.nextLife,
                    waitMs: Math.max(0, Math.min(1000, Math.round(session.startDelay ?? ((session.nextFrame || 0) - Date.now())))) }
            }
        };
    }
}

// Reuse only the engine's draw methods. Constructors and update methods have
// gameplay/audio side effects, so playback must never call them or replace Game.session.
export function renderReplaySnapshot(snapshot) {
    const { state, layout } = snapshot;
    const field = Object.assign(Object.create(PlayingField.prototype), {
        layout: Array.from({ length: BOARD_HEIGHT }, (_, y) => layout.slice(y * BOARD_WIDTH, (y + 1) * BOARD_WIDTH).split('')),
        time: state.status.time, winning: state.status.winning,
        player: Object.assign(Object.create(Player.prototype), state.player),
        rocks: state.rocks.map(rock => Object.assign(Object.create(Rock.prototype), rock))
    });
    const session = Object.assign(Object.create(GameSession.prototype), state.status, { field });
    const liveScreen = Screen.screen;
    try {
        Screen.screen = Array.from({ length: REPLAY_HEIGHT }, () => Array(REPLAY_WIDTH).fill(' '));
        session.draw();
        return Screen.screen.map(row => row.slice(0, REPLAY_WIDTH).join('')).join('');
    } finally { Screen.screen = liveScreen; }
}

/** A displayed state can be watched even when it is too late to take control. */
export function replayTakeoverIssue(snapshot) {
    const { player, rocks, status } = snapshot.state;
    if (status.winning || status.testResult === 'completed') return 'This board is already complete. Rewind to an earlier frame to play.';
    if (status.lives <= 0 || status.time <= 0 || status.testResult || [11, 12].includes(player.state)) return 'The player is dying or the attempt has ended. Rewind to before the loss.';
    if (![1, 2, 3, 4, 5, 6, 8, 9, 10].includes(player.state) ||
        ([8, 9, 10].includes(player.state) && player.jumpStep > 5) ||
        (player.nextState !== null && ![1, 2, 3, 4, 5, 6, 7].includes(player.nextState)) ||
        rocks.some(rock => ![1, 3, 4, 5, 6, 11, 12, 13].includes(rock.state) ||
            (rock.nextState !== null && ![1, 3, 4, 5, 6].includes(rock.nextState)))) {
        return 'This frame contains movement states that cannot be resumed. Choose another frame.';
    }
    if (!snapshot.layout.includes('$')) return 'This frame has no treasure to reach. Choose another frame.';
    return '';
}

/** A separate, unranked, one-board attempt. The source replay is never advanced or edited. */
export class ReplayPracticeSession extends GameSession {
    constructor(snapshot, { onTestEnd, elapsedMs = 0 } = {}) {
        const clean = validateReplaySnapshot(snapshot);
        const issue = replayTakeoverIssue(clean);
        if (issue) throw new Error(issue);
        const { state, layout } = clean, { status } = state;
        const rows = Array.from({ length: BOARD_HEIGHT }, (_, y) => layout.slice(y * BOARD_WIDTH, (y + 1) * BOARD_WIDTH));
        super([{ name: state.name || 'Replay practice', layout: rows }], { testing: true, originalCampaign: false, onTestEnd });
        this.replayPractice = true;
        this.cheated = true;
        this.score = status.score;
        this.lives = status.lives;
        this.levelNumber = status.levelNumber;
        this.playSpeed = status.playSpeed;
        this.practiceDifficulty = state.resume?.difficulty ?? 0;
        this.levelCycle = this.practiceDifficulty + 1;
        this.nextLife = state.resume?.nextLife ?? Math.min(Number.MAX_SAFE_INTEGER, (Math.floor(this.score / NEW_LIFE_SCORE) + 1) * NEW_LIFE_SCORE);
        this.paused = true;
        this.startDelay = Math.max(0, (state.resume?.waitMs ?? this.moveFrameMillisecondDelay()) - Math.max(0, Number.isFinite(elapsedMs) ? elapsedMs : 0));
        const field = this.field = Object.assign(Object.create(PlayingField.prototype), {
            name: state.name, layout: rows.map(row => row.split('')), time: status.time, winning: false, openEdges: false,
            player: Object.assign(new Player(state.player.x, state.player.y), state.player),
            rocks: state.rocks.map(rock => Object.assign(new Rock(rock), rock)), dispensers: []
        });
        field.rockTerrain = Object.create(field);
        field.rockTerrain.openEdges = true;
        for (let y = 0; y < BOARD_HEIGHT; y++) for (let x = 0; x < BOARD_WIDTH; x++) {
            if (field.layout[y][x] === 'V') field.dispensers.push({ x, y });
        }
    }
    hiddenFactor() { return this.practiceDifficulty; }
    moveFrameMillisecondDelay() {
        const delay = PLAY_SPEEDS[this.playSpeed - 1];
        return Math.floor(delay - this.hiddenFactor() * HIDDEN_FACTOR_PLAY_SPEED * delay);
    }
    update() {
        if (this.testResult) return;
        if (this.paused) {
            if (![Input.Action.PAUSE, Input.Action.RESUME].includes(Input.lastAction())) return;
            this.paused = false;
            Input.consume();
        }
        if (this.startDelay !== undefined) {
            this.nextFrame = Date.now() + this.startDelay;
            this.startDelay = undefined;
        }
        super.update();
    }
    // A loss ends this practice attempt; Retry restores the chosen moment, not a new map.
    restartLevel() { this.finishTest('failed'); }
    finishGame() { return null; }
}
